"use client";

import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { useSession } from "@/hooks/use-session";
import { DotGrid } from "@/components/landing/dot-grid";
import { LangSwitcher } from "@/components/landing/landing-nav";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { useSiteFlavor } from "@/components/layout/site-flavor-provider";
import { mockupsFor } from "@/lib/brand";
import { cn } from "@/lib/utils";
import posthog from "posthog-js";
import { SignupCta, useSignupFlow } from "@/components/landing/signup-flow";
import { MockupPicture } from "@/components/landing/mockup-picture";

export function LandingHero() {
  const t = useTranslations("landing");
  const { isAuthenticated } = useSession();
  const { brand } = useSiteFlavor();
  // Телефоны на герое — снимки интерфейса, поэтому набор берёт локаль.
  const mockups = mockupsFor(brand, useLocale());
  const { resolvedTheme } = useTheme();
  const navigates = useSignupFlow().href !== null;
  const [loading, setLoading] = useState(false);

  return (
    <section
      className={cn(
        "relative overflow-hidden pt-16 sm:pt-32",
        // Without the mockup the section would keep the gap the image used to
        // fill, which reads as a failed load rather than a deliberate layout.
        mockups.hero ? "pb-20" : "pb-4",
      )}
    >
      {/* Interactive dot grid */}
      <DotGrid />
      {/* Hero glow */}
      <div className="hero-glow absolute inset-0" />

      <div className="relative mx-auto max-w-7xl px-6">
        {/* Mobile: language + theme controls right below the fixed nav */}
        <div className="sm:hidden flex items-center justify-end gap-2 pb-12">
          <LangSwitcher />
          <ThemeToggle />
        </div>
        <div className="flex flex-col items-center text-center">
          {/* Badge */}
          <div className="animate-fade-up mb-8 flex items-center gap-2 rounded-full border border-landing-border bg-landing-card px-4 py-2">
            <span className="animate-pulse-dot inline-block h-2 w-2 rounded-full bg-mint" />
            <span className="text-sm text-landing-muted">
              {t("hero.badge")}
            </span>
          </div>

          {/* Heading */}
          <h1 className="animate-fade-up delay-100 max-w-4xl font-extrabold tracking-[-0.025em] text-ink" style={{ fontSize: "clamp(2.5rem, 5vw, 4.625rem)", lineHeight: 1.05 }}>
            {t("hero.headingLine1")}
            <br />
            <span className="font-serif italic" style={{ fontSize: "0.75em", color: resolvedTheme === "dark" ? "#34d399" : "#10b981" }}>
              {t("hero.headingLine2")}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="animate-fade-up delay-200 mt-6 max-w-2xl text-lg text-landing-muted leading-relaxed">
            {t("hero.subtitle")}
          </p>

          {/* CTAs */}
          <div className="animate-fade-up delay-300 mt-10 flex flex-wrap items-center justify-center gap-4">
            <SignupCta
              onPress={() => {
                posthog.capture("landing_click", { element: "cta_hero" });
                if (!isAuthenticated) posthog.capture("sign_in_clicked", { source: "hero" });
                // The spinner means "we are navigating"; opening the tour is
                // instant, so it would just flash.
                if (navigates) setLoading(true);
              }}
              className="animate-glow-pulse relative inline-flex items-center justify-center gap-2.5 overflow-hidden rounded-full bg-mint px-8 py-3.5 text-base font-semibold text-[#052e25] transition-all hover:bg-mint-dark active:scale-95 active:opacity-80 touch-manipulation"
              style={{ minWidth: "220px" }}
            >
              {loading ? (
                <>
                  <span className="inline-block h-4 w-4 shrink-0 rounded-full border-2 border-[#052e25]/30 border-t-[#052e25] animate-spin" />
                  <span>{t("hero.ctaLoading")}</span>
                </>
              ) : (
                t("hero.ctaPrimary")
              )}
            </SignupCta>
          </div>

          {/* Phone mockups — omitted on surfaces with no screenshots of their
              own rather than falling back to another brand's. */}
          {mockups.hero && (
            <div className="animate-scale-in delay-400 mt-16 flex justify-center">
              <MockupPicture
                src={resolvedTheme === "dark" ? mockups.hero.dark : mockups.hero.light}
                alt={t("hero.mockupAlt")}
                sizes="(max-width: 768px) 100vw, 720px"
                priority
                className={resolvedTheme === "dark" ? "h-auto w-full max-w-[720px] rounded-3xl" : "h-auto w-full max-w-[720px]"}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
